/**
 * useClientRealtime — secure ticket WebSocket for the client dashboard.
 *
 * After client auth is ready (ClientProtectedRoute), connects private channel
 * `orders` (and optionally `portfolio`) via POST /api/auth/ws-ticket + wss edge.
 * On order/fill/portfolio events, lightly invalidates HTTP portfolio state.
 *
 * Does not replace HTTP health (`useConnectionStatus`); that remains the
 * source of truth for "Live data" / data-load gating.
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from '@/domains/market-data/hooks/useWebSocket';
import type { WsChannel } from '@/infrastructure/websocket/secureSocket';

// =============================================================================
// TYPES
// =============================================================================

export type ClientRealtimeStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'error';

export interface UseClientRealtimeConfig {
  /**
   * When true, mint ticket + open private sockets.
   * Gate on client auth ready (user present under ClientProtectedRoute).
   */
  enabled?: boolean;
  /** Private channels to open. Default: orders only. */
  channels?: WsChannel[];
  /**
   * Called (debounced) when a relevant realtime event arrives.
   * Dashboard should refetch portfolio / positions / orders.
   */
  onInvalidate?: () => void;
  /** Debounce window for onInvalidate (ms). Default 400. */
  invalidateDebounceMs?: number;
}

export interface UseClientRealtimeReturn {
  /** Aggregated status across configured channels (worst of active sockets). */
  status: ClientRealtimeStatus;
  /** True when at least the primary channel is connected. */
  isRealtimeConnected: boolean;
  /** Per-channel connection status. */
  channelStatus: Record<string, ClientRealtimeStatus>;
  /** Last error message from a socket, if any. */
  error: string | null;
  /** Last event timestamp (ISO) received on any channel. */
  lastEventAt: string | null;
  /** Count of processed business events (not ping/pong). */
  eventCount: number;
  connect: () => void;
  disconnect: () => void;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Legacy `type` values (older portal/mock frames).
 * Live QTP uses envelope: `{ type: "event"|"snapshot", event: "order.created", ... }`.
 */
const INVALIDATE_TYPES = new Set([
  'order_update',
  'order_updates',
  'fill',
  'order_fills',
  'execution',
  'portfolio_update',
  'portfolio',
  'position_update',
  'positions',
  'balance_update',
  // QTP envelope type for business frames (narrowed further via `event`)
  'event',
]);

/** QTP `event` names that should trigger HTTP portfolio/orders refetch. */
const INVALIDATE_EVENTS = new Set([
  'order.created',
  'order.updated',
  'order.filled',
  'order.cancelled',
  'execution.fill',
  'portfolio.updated',
  'position.updated',
  'balance.updated',
]);

function shouldInvalidateFrame(msg: {
  type?: string;
  event?: string;
}): boolean {
  const type = (msg.type || '').toLowerCase();
  const event = (msg.event || '').toLowerCase();

  // Handshake / control — never invalidate
  if (
    type === 'connected' ||
    type === 'subscribed' ||
    type === 'heartbeat' ||
    type === 'pong' ||
    type === 'ping' ||
    type === 'snapshot' // initial state; HTTP already loads dashboard
  ) {
    return false;
  }

  // QTP envelope: type=event + event=order.*|portfolio.*|execution.*
  if (event && INVALIDATE_EVENTS.has(event)) {
    return true;
  }
  if (event.startsWith('order.') || event.startsWith('portfolio.') || event.startsWith('execution.') || event.startsWith('position.')) {
    return true;
  }

  // Legacy flat type names
  if (type && INVALIDATE_TYPES.has(type) && type !== 'event') {
    return true;
  }

  return false;
}

function mapWsStatus(
  status: 'connecting' | 'connected' | 'disconnected' | 'error'
): ClientRealtimeStatus {
  return status;
}

function aggregateStatus(
  statuses: ClientRealtimeStatus[],
  enabled: boolean
): ClientRealtimeStatus {
  if (!enabled || statuses.length === 0) return 'idle';
  if (statuses.some((s) => s === 'connected')) return 'connected';
  if (statuses.some((s) => s === 'connecting')) return 'connecting';
  if (statuses.some((s) => s === 'error')) return 'error';
  return 'disconnected';
}

// =============================================================================
// HOOK
// =============================================================================

/** Stable heartbeat payload — avoid recreating connect() every render. */
const HEARTBEAT_PING = { type: 'ping' } as const;

const ORDERS_RECONNECT = {
  enabled: true,
  maxAttempts: 10,
  delay: 1000,
  backoffMultiplier: 1.5,
} as const;

const PORTFOLIO_RECONNECT = {
  enabled: true,
  maxAttempts: 8,
  delay: 1200,
  backoffMultiplier: 1.5,
} as const;

const HEARTBEAT_CFG = {
  enabled: true,
  interval: 30_000,
  message: HEARTBEAT_PING,
} as const;

export function useClientRealtime(
  config: UseClientRealtimeConfig = {}
): UseClientRealtimeReturn {
  const {
    enabled = true,
    channels = ['orders'],
    onInvalidate,
    invalidateDebounceMs = 400,
  } = config;

  // QTP path channels: /ws/orders, /ws/portfolio (see WS_TICKET_CHANNELS)
  const primaryChannel: WsChannel =
    (channels.find((c) => c !== 'portfolio') as WsChannel | undefined) ?? 'orders';
  const wantPortfolio = channels.includes('portfolio');

  const [lastEventAt, setLastEventAt] = useState<string | null>(null);
  const [eventCount, setEventCount] = useState(0);

  const onInvalidateRef = useRef(onInvalidate);
  onInvalidateRef.current = onInvalidate;
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleInvalidate = useCallback(() => {
    if (!onInvalidateRef.current) return;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      onInvalidateRef.current?.();
    }, invalidateDebounceMs);
  }, [invalidateDebounceMs]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleMessage = useCallback(
    (data: unknown) => {
      if (!data || typeof data !== 'object') return;
      const msg = data as { type?: string; event?: string };
      if (!msg.type || msg.type === 'pong' || msg.type === 'ping') return;
      // Handshake / ack noise
      if (
        msg.type === 'subscribed' ||
        msg.type === 'hello' ||
        msg.type === 'ack' ||
        msg.type === 'heartbeat'
      ) {
        return;
      }

      // Count business frames (snapshots + events) for Realtime chip activity
      if (msg.type === 'connected' || msg.type === 'snapshot' || msg.type === 'event') {
        setEventCount((c) => c + 1);
        setLastEventAt(new Date().toISOString());
      } else if (msg.type) {
        setEventCount((c) => c + 1);
        setLastEventAt(new Date().toISOString());
      }

      if (shouldInvalidateFrame(msg)) {
        scheduleInvalidate();
      }
    },
    [scheduleInvalidate]
  );

  // Primary private channel (orders). softFailAuth: true — do not hard-redirect
  // away from the dashboard when mint races; chip stays "off" and reconnects.
  // requireTicket remains true (private path never opens without a ticket).
  const ordersWs = useWebSocket(
    {
      channel: primaryChannel,
      useTicket: true,
      requireTicket: true,
      softFailAuth: true,
      reconnect: ORDERS_RECONNECT,
      heartbeat: HEARTBEAT_CFG,
    },
    handleMessage
  );

  // Optional portfolio channel — path /ws/portfolio (QTP)
  const portfolioWs = useWebSocket(
    {
      channel: 'portfolio',
      useTicket: true,
      requireTicket: true,
      softFailAuth: true,
      reconnect: PORTFOLIO_RECONNECT,
      heartbeat: HEARTBEAT_CFG,
    },
    handleMessage
  );

  // Connect only after client session is ready (enabled ← user?.id under ClientProtectedRoute).
  // Capture stable connect/disconnect via refs so we do not thrash on identity changes.
  const ordersConnectRef = useRef(ordersWs.connect);
  const ordersDisconnectRef = useRef(ordersWs.disconnect);
  const portfolioConnectRef = useRef(portfolioWs.connect);
  const portfolioDisconnectRef = useRef(portfolioWs.disconnect);
  ordersConnectRef.current = ordersWs.connect;
  ordersDisconnectRef.current = ordersWs.disconnect;
  portfolioConnectRef.current = portfolioWs.connect;
  portfolioDisconnectRef.current = portfolioWs.disconnect;

  useEffect(() => {
    if (!enabled) {
      ordersDisconnectRef.current();
      if (wantPortfolio) portfolioDisconnectRef.current();
      return;
    }

    ordersConnectRef.current();
    if (wantPortfolio) {
      portfolioConnectRef.current();
    }

    return () => {
      ordersDisconnectRef.current();
      if (wantPortfolio) portfolioDisconnectRef.current();
    };
  }, [enabled, wantPortfolio, primaryChannel]);

  // Subscribe once connected — path channel already registered; subscribe re-affirms topics
  useEffect(() => {
    if (!enabled || ordersWs.state.status !== 'connected') return;

    ordersWs.subscribe('order_update', () => {});
    ordersWs.subscribe('fill', () => {});
    ordersWs.subscribe('execution', () => {});
    // QTP subscribeTarget reads `channel` (string), not `channels` array
    ordersWs.send({ type: 'subscribe', channel: 'orders' });
    ordersWs.send({ type: 'subscribe', channel: 'executions' });
  }, [enabled, ordersWs.state.status, ordersWs.subscribe, ordersWs.send]);

  useEffect(() => {
    if (!enabled || !wantPortfolio || portfolioWs.state.status !== 'connected') {
      return;
    }

    portfolioWs.subscribe('portfolio_update', () => {});
    portfolioWs.subscribe('position_update', () => {});
    portfolioWs.send({ type: 'subscribe', channel: 'portfolio' });
  }, [
    enabled,
    wantPortfolio,
    portfolioWs.state.status,
    portfolioWs.subscribe,
    portfolioWs.send,
  ]);

  const channelStatus: Record<string, ClientRealtimeStatus> = {
    [primaryChannel]: enabled
      ? mapWsStatus(ordersWs.state.status)
      : 'idle',
  };
  if (wantPortfolio) {
    channelStatus.portfolio = enabled
      ? mapWsStatus(portfolioWs.state.status)
      : 'idle';
  }

  const status = aggregateStatus(Object.values(channelStatus), enabled);
  const isRealtimeConnected = status === 'connected';
  const error =
    ordersWs.state.error ||
    (wantPortfolio ? portfolioWs.state.error : null) ||
    null;

  const connect = useCallback(() => {
    ordersWs.connect();
    if (wantPortfolio) portfolioWs.connect();
  }, [ordersWs, portfolioWs, wantPortfolio]);

  const disconnect = useCallback(() => {
    ordersWs.disconnect();
    if (wantPortfolio) portfolioWs.disconnect();
  }, [ordersWs, portfolioWs, wantPortfolio]);

  return {
    status,
    isRealtimeConnected,
    channelStatus,
    error,
    lastEventAt,
    eventCount,
    connect,
    disconnect,
  };
}

export default useClientRealtime;
